import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  GenerateMappingResult,
  DimensionMapping,
  MetricMapping,
  ExistingMetricMapping,
  NewMetricMapping,
} from '../types/generate-mapping';

interface MappingRow {
  sourceField: string;
  rubiiField: string;
  rubiiFieldLabel: string;
  category: string;
  type: 'dimension' | 'metric';
  status: 'mapped' | 'derived' | 'auto-generated' | 'unmapped';
  reason?: string;
  isIgnored: boolean;
  mapping: DimensionMapping | MetricMapping;
}

@Component({
  selector: 'app-mapping-result',
  templateUrl: './mapping-result.component.html',
  styleUrls: ['./mapping-result.component.scss']
})
export class MappingResultComponent implements OnInit {

  clientId = '';
  result: GenerateMappingResult | null = null;
  rows: MappingRow[] = [];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
  ) { }

  ngOnInit(): void {
    const uuid = this.route.snapshot.paramMap.get('uuid');
    if (!uuid) {
      this.router.navigate(['/datasources/custom/upload']);
      return;
    }
    this.clientId = uuid;

    const stored = sessionStorage.getItem('data-source-custom-mapping-result-' + this.clientId);
    if (stored) {
      try {
        this.result = JSON.parse(stored);
        this.rows = this.buildRows(this.result!);
      } catch {
        this.router.navigate(['/datasources/custom/upload']);
      }
    } else {
      this.router.navigate(['/datasources/custom/upload']);
    }
  }

  private static readonly DIM_CATEGORIES: Record<string, string> = {
    advertiserName: 'Advertiser', advertiserId: 'Advertiser',
    campaignName: 'Campaign', campaignId: 'Campaign',
    lineItemName: 'Line Item', lineItemId: 'Line Item',
    creativeName: 'Creative', creativeId: 'Creative',
    date: 'Date',
  };

  private buildRows(result: GenerateMappingResult): MappingRow[] {
    const rows: MappingRow[] = [];

    for (const [key, dim] of Object.entries(result.dimensions)) {
      const category = MappingResultComponent.DIM_CATEGORIES[key] ?? 'Dimension';
      if (dim.type === 'auto-generate') {
        rows.push({
          sourceField: '(auto-generated)',
          rubiiField: key,
          rubiiFieldLabel: this.stripDelimiters(dim.dimensionName),
          category,
          type: 'dimension',
          status: 'auto-generated',
          reason: 'No matching column — value will be generated',
          isIgnored: false,
          mapping: dim,
        });
      } else if (dim.type === 'clone') {
        rows.push({
          sourceField: `(clone from ${this.stripDelimiters(dim.from ?? '')})`,
          rubiiField: key,
          rubiiFieldLabel: this.stripDelimiters(dim.dimensionName),
          category,
          type: 'dimension',
          status: 'auto-generated',
          reason: `Copied from ${this.stripDelimiters(dim.from ?? '')}`,
          isIgnored: false,
          mapping: dim,
        });
      } else {
        const rawSource = dim.name ?? dim.value ?? key;
        rows.push({
          sourceField: this.stripDelimiters(rawSource),
          rubiiField: key,
          rubiiFieldLabel: this.stripDelimiters(dim.dimensionName),
          category,
          type: 'dimension',
          status: 'mapped',
          isIgnored: false,
          mapping: dim,
        });
      }
    }

    for (const [key, metric] of Object.entries(result.metrics)) {
      if (metric.exist) {
        const existing = metric as ExistingMetricMapping;
        rows.push({
          sourceField: this.stripDelimiters(key),
          rubiiField: this.stripDelimiters(existing.metricName),
          rubiiFieldLabel: this.stripDelimiters(existing.metricName),
          category: 'Number',
          type: 'metric',
          status: 'mapped',
          isIgnored: false,
          mapping: metric,
        });
      } else if ((metric as NewMetricMapping).derived) {
        const newMetric = metric as NewMetricMapping;
        rows.push({
          sourceField: this.stripDelimiters(key),
          rubiiField: this.stripDelimiters(key),
          rubiiFieldLabel: this.stripDelimiters(key),
          category: newMetric.type === 'currency' ? 'Currency' : newMetric.type === 'percentage' ? 'Percentage' : 'Number',
          type: 'metric',
          status: 'derived',
          reason: newMetric.formula ? this.stripDelimiters(newMetric.formula) : undefined,
          isIgnored: false,
          mapping: metric,
        });
      } else {
        const newMetric = metric as NewMetricMapping;
        rows.push({
          sourceField: this.stripDelimiters(key),
          rubiiField: this.stripDelimiters(key),
          rubiiFieldLabel: this.stripDelimiters(key),
          category: newMetric.type === 'currency' ? 'Currency' : newMetric.type === 'percentage' ? 'Percentage' : 'Number',
          type: 'metric',
          status: 'unmapped',
          reason: `${newMetric.type} metric — not matched to a Rubii metric`,
          isIgnored: false,
          mapping: metric,
        });
      }
    }

    return rows;
  }

  private stripDelimiters(value: string): string {
    return value.replace(/\|#\|/g, '');
  }

  getStatusIcon(status: MappingRow['status']): string {
    switch (status) {
      case 'mapped': return 'check_circle';
      case 'derived': return 'functions';
      case 'auto-generated': return 'auto_fix_high';
      case 'unmapped': return 'cancel';
      default: return 'help_outline';
    }
  }

  getStatusClass(status: MappingRow['status']): string {
    return `status--${status}`;
  }

  toggleIgnore(index: number): void {
    this.rows[index].isIgnored = !this.rows[index].isIgnored;
  }

  get dimensionRows(): MappingRow[] {
    return this.rows.filter(r => r.type === 'dimension');
  }

  get metricRows(): MappingRow[] {
    return this.rows.filter(r => r.type === 'metric');
  }

  get mappedCount(): number {
    return this.rows.filter(r => r.status === 'mapped' && !r.isIgnored).length;
  }

  get totalCount(): number {
    return this.rows.length;
  }

  get ignoredRows(): MappingRow[] {
    return this.rows.filter(r => r.isIgnored);
  }

  goBack(): void {
    this.router.navigate(['/datasources/custom/summary', this.clientId]);
  }

  save(): void {
    sessionStorage.setItem('data-source-custom-mapping-result-' + this.clientId, JSON.stringify(this.result));
    // TODO: call backend to save mappings
  }
}
